import React, { useMemo } from 'react';

import { Box, Chip, Paper, Stack, Typography } from '@mui/material';

import { getPackageInclusions } from '../../utils';

interface Props {
  offerPackage: App.Package;
}

interface PackageInclusionRecord {
  inclusion?: {
    name?: string;
    is_hidden?: boolean;
    category?: {
      name?: string;
    };
  };
  category?: string | { name?: string };
  content?: string;
  name?: string;
  style?: string | null;
  lux_plus_tier?: string | null;
}

function isInclusionRecord(value: unknown): value is PackageInclusionRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normaliseInclusion(value: unknown): PackageInclusionRecord {
  if (isInclusionRecord(value)) {
    return value;
  }

  if (typeof value === 'string') {
    return { name: value };
  }

  return {};
}

function getInclusionCategory(inclusion: PackageInclusionRecord) {
  if (typeof inclusion.category === 'string') {
    return inclusion.category;
  }

  return inclusion.inclusion?.category?.name || inclusion.category?.name || 'Other';
}

function getInclusionName(inclusion: PackageInclusionRecord) {
  return inclusion.inclusion?.name || inclusion.name || inclusion.content || 'Untitled inclusion';
}

export default function PackageInclusionsTab({ offerPackage }: Props) {
  const inclusions = useMemo(() => getPackageInclusions(offerPackage).map(normaliseInclusion), [offerPackage]);

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Box
          sx={{
            bgcolor: 'grey.50',
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', md: 'minmax(180px, 0.8fr) minmax(280px, 1.2fr) 220px' },
            px: 2,
            py: 1.5,
          }}
        >
          <Typography variant="overline" sx={{ fontWeight: 700 }}>
            Category
          </Typography>
          <Typography variant="overline" sx={{ fontWeight: 700 }}>
            Inclusion
          </Typography>
          <Typography variant="overline" sx={{ fontWeight: 700 }}>
            Labels
          </Typography>
        </Box>

        {inclusions.length > 0 ? (
          inclusions.map((inclusion, index) => (
            <Box
              key={`${getInclusionName(inclusion)}-${index}`}
              sx={{
                borderTop: 1,
                borderColor: 'divider',
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', md: 'minmax(180px, 0.8fr) minmax(280px, 1.2fr) 220px' },
                px: 2,
                py: 2,
              }}
            >
              <Typography variant="body2">{getInclusionCategory(inclusion)}</Typography>
              <Typography variant="body2">{getInclusionName(inclusion)}</Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {inclusion.style === 'highlighted' && <Chip size="small" label="Highlighted" />}
                {inclusion.lux_plus_tier && <Chip size="small" label="LuxPlus only" />}
                {inclusion.inclusion?.is_hidden && <Chip size="small" label="Hidden" />}
              </Stack>
            </Box>
          ))
        ) : (
          <Box sx={{ borderTop: 1, borderColor: 'divider', px: 2, py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No inclusions added.
            </Typography>
          </Box>
        )}
      </Paper>
    </Stack>
  );
}
